import React, { lazy, Suspense } from "react";
import { Box } from "@mui/material";
import Navbar from "../widgets/navbar/navbar";
import MenuDrawer from "../../../Components/Widgets/MenuDrawer";
import SubMenuDrawer from "../../../Components/Widgets/SubMenuDrawer";
import Footer from "../widgets/footer";
import RevealSection from "../widgets/common/reveal-section";
import { HeroPlaceholder } from "../widgets/common/home-section-skeleton";
import { useTheme } from "@mui/material/styles";

const HomeHeroSlide = lazy(
  () => import("../../default/widgets/sliders/home-hero-slide"),
);

const SplitFeatureSection = lazy(
  () => import("../../default/widgets/sliders/split-feature-section"),
);

const ImageBackroundEffect = lazy(
  () => import("../../default/widgets/sliders/image-backround-effect"),
);

const ProductsShowcaseSlider = lazy(
  () => import("../../../Components/Widgets/Sliders/ProductsShowcaseSlider"),
);

const ShopCategoryGrid = lazy(
  () => import("../../default/widgets/sliders/shop-category-grid"),
);

// const CollectionSlider = lazy(
//   () => import("../../default/widgets/sliders/collection-slider"),
// );

const Grid2Section = lazy(
  () => import("../../default/widgets/sliders/k-world-grid-2"),
);

const ExploreMoreSection = lazy(
  () => import("../../default/widgets/sliders/explore-more-section"),
);

const RecentlyViewed = lazy(
  () => import("../../../Components/Widgets/RecentlyViewed"),
);

const ExportDefault = (props) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <Navbar bg_color="#fff" {...props} />
      <MenuDrawer {...props} />
      <SubMenuDrawer {...props} />
      <Suspense fallback={<HeroPlaceholder theme={theme} />}>
        <HomeHeroSlide
          {...{
            ...props,
            mb: { xs: 6, md: 8 },
            slide: {
              videoPath:
                "https://files.automatapp.com/konstantino/assets/summer-video.mp4",
              title: "TIMELESS BY DESIGN",
              desc: "Explore signature creations that bring Greek artistry to life. From bold statements to everyday essentials, discover jewelry with enduring character and a story in every detail.",
              descMaxWidth: { xs: "300px", sm: "540px" },
              buttons: [
                {
                  name: "DISCOVER WOMEN'S",
                  url: "/women",
                },
                { name: "DISCOVER MEN'S", url: "/men" },
              ],
            },
          }}
        />
      </Suspense>

      <RevealSection id="nedaSection" variant="slider">
        <SplitFeatureSection
          {...{
            ...props,
            reverse: false,
            imagesPath: "/marketing/main",
            images: {
              main: "neda-main",
              desktopBackground: "neda-bg",
              secondary: "neda-secondary",
            },
            content: {
              title: "NEDA COLLECTION",
              desc: "Explore our signature style: sterling silver masterpieces adorned with radiant 18k gold touches that define our legacy.",
              descMaxWidth: { xs: "unset", sm: "500px" },
              url: "/women/collection/neda",
              button: "DISCOVER NEDA",
            },
          }}
        />
      </RevealSection>

      <RevealSection id="nedaProducts" variant="products">
        <ProductsShowcaseSlider
          {...{
            ...props,
            mb: { xs: 6, md: 8 },
            params: {
              pageAmount: 100,
              collection: "neda",
              gender: "women",
              type: "ring,bracelet,earrings,pendant,necklace",
            },
            amount: 15,
            shuffle: true,
            productDisplay: {
              showName: false,
              showCollection: false,
              showPrice: false,
              showGems: false,
              showCuration: true,
            },
          }}
        />
      </RevealSection>

      <RevealSection
        id="imageBackground"
        variant="banner"
        minHeight="100svh"
        mode="load-only"
      >
        <ImageBackroundEffect
          {...{
            ...props,
            ...{
              slide: {
                slug: "oceanic-blues",
                url: "/",
                imagesPath: "/marketing/main",
                content: {
                  title: "OCEANIC BLUES",
                  desc: "Explore our curation of blue stone designs, inspired by the richness and rhythm of the Aegean Sea. Made for sunlit days and warm summer nights.",
                  descMaxWidth: { xs: "unset", sm: "420px" },
                  buttons: [
                    {
                      name: "SHOP WOMEN'S BLUE DESIGNS",
                      url: "/women/curation/blue-statements",
                    },
                    {
                      name: "SHOP MEN'S BLUE DESIGNS",
                      url: "/men/curation/blue-statements",
                    },
                  ],
                  position: {
                    mobile: {
                      bottom: "40px",
                      left: "24px",
                      right: "24px",
                    },
                    desktop: {
                      top: "60px",
                      left: { md: "40px", lg: "120px" },
                    },
                  },
                },
              },
            },
          }}
        />
      </RevealSection>

      <RevealSection id="categoriesSlider1" variant="slider">
        <ShopCategoryGrid
          {...{
            ...props,
            mt: { xs: 4, md: 4 },
            mb: { xs: 6, md: 8 },
            title: "SHOP BY CATEGORY",
            imagesPath: "/marketing/main",
            tabs: [
              {
                slug: "women",
                name: "WOMEN'S",
                url: "/women/types/all",
                slides: [
                  {
                    slug: "womens-rings",
                    title: "Rings",
                    url: "/women/type/ring",
                  },
                  {
                    slug: "womens-pendants",
                    title: "Pendants",
                    url: "/women/type/pendant",
                  },
                  {
                    slug: "womens-bracelets",
                    title: "Bracelets",
                    url: "/women/type/bracelet",
                  },
                  {
                    slug: "womens-earrings",
                    title: "Earrings",
                    url: "/women/type/earrings",
                  },
                  {
                    slug: "womens-necklaces",
                    title: "Necklaces",
                    url: "/women/type/necklace",
                  },
                  {
                    slug: "womens-shop-all",
                    title: "All",
                    url: "/women/types/all",
                    showOnDesktop: false,
                  },
                ],
              },
              {
                slug: "men",
                name: "MEN'S",
                url: "/men/types/all",
                slides: [
                  {
                    slug: "mens-rings",
                    title: "Rings",
                    url: "/men/type/ring",
                  },
                  {
                    slug: "mens-pendants",
                    title: "Pendants & Talismans",
                    url: "/men/type/pendant",
                  },
                  {
                    slug: "mens-bracelets",
                    title: "Bracelets",
                    url: "/men/type/bracelet",
                  },
                  {
                    slug: "mens-earrings",
                    title: "Earrings",
                    url: "/men/type/earrings",
                  },
                  {
                    slug: "mens-necklaces",
                    title: "Necklaces & Chains",
                    url: "/men/type/necklace",
                  },
                  {
                    slug: "mens-shop-all",
                    title: "All",
                    url: "/men/types/all",
                    showOnDesktop: false,
                  },
                ],
              },
            ],
          }}
        />
      </RevealSection>

      <RevealSection id="kavosSection" variant="slider">
        <SplitFeatureSection
          {...{
            ...props,
            reverse: true,
            imagesPath: "/marketing/main",
            images: {
              main: "kavos-main",
              desktopBackground: "kavos-bg",
              secondary: "kavos-secondary",
            },
            content: {
              title: "LAYER WITH KAVOS",
              desc: "Explore beaded, leather, and chain link pieces from Kavos, designed to bring texture, contrast, and character to every layer.",
              descMaxWidth: "420px",
              url: "/men/collection/kavos",
              button: "EXPLORE KAVOS",
            },
          }}
        />
      </RevealSection>

      <RevealSection id="kavosProducts" variant="products">
        <ProductsShowcaseSlider
          {...{
            ...props,
            mb: { xs: 6, md: 8 },
            params: {
              pageAmount: 100,
              collection: "kavos",
              gender: "men",
              type: "bracelet",
            },
            amount: 15,
            shuffle: true,
            productDisplay: {
              showName: false,
              showCollection: false,
              showPrice: false,
              showGems: false,
              showCuration: true,
            },
          }}
        />
      </RevealSection>

      <RevealSection id="featuredSlider" variant="slider">
        <ExploreMoreSection
          {...{
            ...props,
            mb: { xs: 6, md: 8 },
            title: "EXPLORE MORE",
            imagesPath: "/marketing/main",
            slides: [
              {
                slug: "main-gold",
                url: "/women/metal/18kt-gold",
                buttons: [
                  {
                    name: "WOMEN'S GOLD",
                    url: "/women/metal/18kt-gold",
                  },
                  {
                    name: "MEN'S GOLD",
                    url: "/men/metal/18kt-gold",
                  },
                ],
              },
              {
                slug: "main-best-seller",
                url: "/women/curation/best-sellers",
                buttons: [
                  {
                    name: "WOMEN'S BEST SELLERS",
                    url: "/women/curation/best-sellers",
                  },
                  {
                    name: "MEN'S BEST SELLERS",
                    url: "/men/curation/best-sellers",
                  },
                ],
              },
              {
                slug: "main-new-designs",
                url: "/women/curation/new-arrivals",
                buttons: [
                  {
                    name: "WOMEN'S NEW DESIGNS",
                    url: "/women/curation/new-arrivals",
                  },
                  {
                    name: "MEN'S NEW DESIGNS",
                    url: "/men/curation/new-arrivals",
                  },
                ],
              },
              {
                slug: "main-gifts",
                url: "/gifts",
                buttons: [
                  {
                    name: "WOMEN'S GIFTS",
                    url: "/gifts",
                  },
                  {
                    name: "MEN'S GIFTS",
                    url: "/gifts",
                  },
                ],
              },
            ],
          }}
        />
      </RevealSection>

      <RevealSection id="grid2Section" variant="grid">
        <Grid2Section
          {...{
            ...props,
            mb: { xs: 6, md: 8 },
          }}
        />
      </RevealSection>

      <RevealSection id="recentlyViewed" variant="products">
        <RecentlyViewed
          {...{
            ...props,
            mb: { xs: 6, md: 8 },
          }}
          title="ITEMS YOU VIEWED"
          productDisplay={{
            showName: false,
            showCollection: false,
            showPrice: false,
            showGems: false,
            showCuration: true,
          }}
        />
      </RevealSection>

      <Footer {...props} />
    </Box>
  );
};

export default ExportDefault;
